/**
 * How to NAME a profession to a human, in the portal.
 *
 * WHY THIS EXISTS (spec 798af82c). An acceptance page is chosen by the
 * invitation's `verificationStrategy`, not by its profession — so
 * `poder_judicial` routes a traductor público to the page the notary flow was
 * written on, whose copy named the escribano eight times over: the title, the
 * description, the credential field and its help text, two validation errors,
 * the step description and the success toast. The first traductor to accept an
 * invitation would be addressed as an escribano throughout their own signup
 * and asked for an escribano's matrícula.
 *
 * THE BACKEND ALREADY SOLVED THIS and the portal did not use it:
 * `apps/backend/src/common/i18n/profession-copy.ts` resolves
 * `profession.<copyNamespace>.<form>` with a neutral fallback. This mirrors it
 * on the client, which is where it has to be — the acceptance route is
 * unauthenticated and carries its own language toggle, so the portal owns
 * locale and a server that rendered the words would have to be told it and
 * re-asked on every switch.
 *
 * KEYED BY `copyNamespace`, NOT BY SLUG. The registry carries the namespace as
 * its own column precisely so copy written by people outlives a slug rename.
 * Both seeded professions set it equal to their slug, so passing a slug works
 * today; callers that hold the invitation payload should pass the namespace it
 * serves, so that stays true when the two diverge.
 */

/**
 * Which form of the name a surface needs.
 *
 * FOUR FORMS, and each exists because a real string needs it:
 * - `noun` — the name as a HEADING renders it ("Escribano/a", "notary").
 * - `nounInSentence` — mid-sentence ("unirte como escribano/a").
 * - `credentialLabel` — the field label, capitalised ("Matrícula").
 * - `credentialNoun` — the credential mid-sentence ("la matrícula es…").
 *
 * NOT DERIVED FROM ONE ANOTHER, for the reason recorded beside the backend's
 * pair: lowercasing a heading gives a word the prose does not use, and
 * capitalising a mid-sentence noun is wrong in both languages. The casing
 * difference is also per-LOCALE — Spanish capitalises the profession in this
 * page's title and English does not — which is exactly the kind of thing a
 * translator decides and a derivation rule cannot.
 *
 * THE CREDENTIAL IS A SEPARATE QUESTION from the profession's name: a register
 * may issue something that is not called a matrícula at all, which is why the
 * word for it is copy rather than being assumed (spec 798af82c, RES-2).
 */
export type ProfessionCopyForm =
  | 'noun'
  | 'nounInSentence'
  | 'credentialLabel'
  | 'credentialNoun';

/** Correct for every profession that will ever exist, which is the point. */
export function professionFallbackKey(form: ProfessionCopyForm): string {
  return `profession.fallback.${form}`;
}

/**
 * The i18n key naming this profession — its own when someone has written it,
 * the neutral one when nobody has yet.
 *
 * RETURNS A KEY, NOT A STRING, mirroring the backend's split. The caller keeps
 * control of interpolation, and of whether a miss should read neutrally (a
 * sentence needs a word regardless) or be omitted entirely (a label row that
 * would print "Profesional — Profesional" carries nothing).
 *
 * PURE GIVEN `exists`, which is why `exists` is a parameter rather than an
 * i18next import: the fallback rule is the thing worth testing, and it is
 * testable here with a fake. The page passes i18next's own `i18n.exists`.
 *
 * A REGISTRY AHEAD OF THE COPY IS EXPECTED, not a defect. A profession can be
 * inserted as data — that is the whole point of the registry — and the words
 * follow when someone writes them. Until then the profession reads as
 * "profesional", which is true of all of them, rather than as whichever other
 * profession happened to be hardcoded.
 */
export function professionCopyKey(
  copyNamespace: string | null | undefined,
  form: ProfessionCopyForm,
  exists: (key: string) => boolean,
): string {
  const namespace = copyNamespace?.trim();
  if (!namespace) return professionFallbackKey(form);
  const key = `profession.${namespace}.${form}`;
  return exists(key) ? key : professionFallbackKey(form);
}
